import {
  Injectable,
  inject,
  DestroyRef,
  RendererFactory2,
  Renderer2,
  Signal,
  OnDestroy
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  Observable,
  switchMap,
  map,
  shareReplay,
  filter,
  take
} from 'rxjs';
import {
  isThemeAwareLayer,
  JmkwEnvironment,
  setThemeRuntimeCurrentTheme,
  ThemeInfo
} from '@jeppesen-foreflight/jmkw-types';
import type { ThemeColor } from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';
import { CONFIG_TOKEN } from '../../jmkw.config';
import { store } from '../../jmkw.store';
import { BasemapManagerService } from '../arcgis';
import { safeGetItem, safeSetItem } from '../utils/safe-local-storage';
import { ThemeRegistryService } from './theme-registry.service';

/** localStorage key holding the user's last selected theme. */
const SELECTED_THEME_STORAGE_KEY = 'jmkw-selected-theme';

@Injectable({ providedIn: 'root' })
export class ThemeService implements OnDestroy {
  private readonly _env = inject(CONFIG_TOKEN) as JmkwEnvironment;
  private readonly _basemapManager = inject(BasemapManagerService);
  private readonly _themeRegistry = inject(ThemeRegistryService);
  private readonly _logger = inject(NGXLogger);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _rendererFactory = inject(RendererFactory2);
  private readonly _renderer: Renderer2 = this._rendererFactory.createRenderer(
    null,
    null
  );
  private readonly _body: HTMLElement = this._renderer.selectRootElement(
    'body',
    true
  );
  private readonly _htmlElement: HTMLElement = this._renderer.selectRootElement(
    'html',
    true
  );

  private _calciteObserver: MutationObserver | null = null;

  /**
   * Theme color most recently pushed to the map layers.
   *
   * The Calcite observer watches `class` and `jmkw-theme` on body/documentElement, and
   * `applySelectedTheme()` writes both of those itself. Without this, JMKW's own theme
   * write re-triggers the observer and re-runs the full layer pass on every switch. The
   * observer exists to catch a *host application* flipping the theme, so only a color that
   * differs from what we last applied is worth acting on.
   */
  private _lastAppliedThemeColor?: ThemeColor;

  private readonly _defaultThemeId =
    this._env?.themeConfig?.defaultTheme ?? 'default-light';
  private readonly _enabledThemes = this._env?.themeConfig?.enabledThemes ?? [
    'default-light',
    'default-dark'
  ];

  private readonly _selectedThemeId$ = new BehaviorSubject<string>(
    this.getInitialTheme()
  );

  public readonly currentTheme$: Observable<ThemeInfo | undefined>;
  public readonly $currentTheme: Signal<ThemeInfo | undefined>;

  public readonly enabledAvailableThemes$: Observable<ThemeInfo[]> =
    this._basemapManager.basemaps$.pipe(
      switchMap((basemaps) =>
        this._themeRegistry.themes$.pipe(
          map((availableThemes) => {
            const filtered = availableThemes.filter((theme) =>
              this._enabledThemes.includes(theme.id)
            );

            if (filtered.length === 0) {
              this._logger.error(
                '[ThemeService] No enabled themes found in registry!',
                {
                  enabledThemes: this._enabledThemes,
                  availableThemes: availableThemes.map((t) => t.id)
                }
              );
            } else if (filtered.length !== this._enabledThemes.length) {
              const missingThemes = this._enabledThemes.filter(
                (id) => !availableThemes.some((theme) => theme.id === id)
              );
              this._logger.warn(
                '[ThemeService] Some enabled themes not found in registry:',
                {
                  missingThemes,
                  foundThemes: filtered.map((t) => t.id)
                }
              );
            }

            return filtered;
          })
        )
      ),
      shareReplay(1)
    );
  public readonly $enabledAvailableThemes: Signal<ThemeInfo[] | undefined>;

  constructor() {
    this.$enabledAvailableThemes = toSignal(this.enabledAvailableThemes$);
    this.currentTheme$ = this.initializeThemeService().pipe(shareReplay(1));

    this.currentTheme$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((selectedTheme) => {
        if (!selectedTheme) {
          this._logger.error('[ThemeService] No theme available to apply!');
          return;
        }
        // Basemap is applied centrally inside applySelectedTheme via
        // applyBasemap(). A previous duplicate setBasemapById call here
        // doubled the Esri Basemap property-change cascade per theme
        // switch, amplifying the dual-view lock symptom (JMKW-1859).
        this.applySelectedTheme(selectedTheme);
      });

    this.$currentTheme = toSignal(this.currentTheme$);
  }

  ngOnDestroy(): void {
    this._calciteObserver?.disconnect();
    this._calciteObserver = null;
  }

  private initializeThemeService(): Observable<ThemeInfo> {
    return this.enabledAvailableThemes$.pipe(
      switchMap((enabledAvailableThemes: ThemeInfo[]) =>
        this._selectedThemeId$.pipe(
          map((selectedThemeId) => {
            let foundTheme = enabledAvailableThemes.find(
              (theme) => selectedThemeId === theme.id
            );

            if (!foundTheme) {
              this._logger.warn(
                `[ThemeService] Selected theme '${selectedThemeId}' not found.`,
                {
                  selectedThemeId,
                  availableThemes: enabledAvailableThemes.map((t) => t.id)
                }
              );

              foundTheme = enabledAvailableThemes.find(
                (theme) => theme.id === this._defaultThemeId
              );

              if (!foundTheme) {
                this._logger.error(
                  `[ThemeService] Default theme '${this._defaultThemeId}' not found either!`,
                  {
                    defaultThemeId: this._defaultThemeId,
                    availableThemes: enabledAvailableThemes.map((t) => t.id)
                  }
                );

                if (enabledAvailableThemes.length > 0) {
                  foundTheme = enabledAvailableThemes[0];
                  this._logger.warn(
                    `[ThemeService] Falling back to first available theme: '${foundTheme.id}'`
                  );

                  this._selectedThemeId$.next(foundTheme.id);
                } else {
                  this._logger.error(
                    '[ThemeService] No themes available at all!'
                  );
                }
              } else {
                this._selectedThemeId$.next(foundTheme.id);
              }
            }

            return foundTheme;
          })
        )
      ),
      filter((themeInfo) => !!themeInfo)
    );
  }

  private applySelectedTheme(selectedTheme: ThemeInfo): void {
    const themeIdToApply = selectedTheme.id;
    const themeColor = this.resolveThemeColor(selectedTheme);
    setThemeRuntimeCurrentTheme(themeColor);
    this._logger.debug('[ThemeService] Theme subscription fired:', {
      id: themeIdToApply
    });

    this.setThemeAttribute(themeIdToApply);
    this.injectCssVariables(selectedTheme, themeIdToApply);

    try {
      const calciteMode = themeColor;
      const htmlEl = document.documentElement;
      const bodyEl = document.body;

      this.applyCalciteMode(calciteMode, htmlEl, bodyEl);
      const calciteVariables = this.buildCalciteVariables(
        selectedTheme,
        calciteMode
      );
      this.applyCalciteVariables(calciteVariables, htmlEl, bodyEl, calciteMode);
      this.syncEsriUiCalciteTheme();
      this.applyCalciteLightDom(calciteVariables);
    } catch (error) {
      this._logger.error(
        '[ThemeService] Failed to set Calcite theme mode:',
        error
      );
    }

    this.applyBasemap(selectedTheme);
    this.applyThemeToMapLayers(themeColor);
  }

  private resolveThemeColor(selectedTheme: ThemeInfo): ThemeColor {
    const values = [
      selectedTheme.id,
      selectedTheme.metadata?.category,
      selectedTheme.metadata?.tags,
      selectedTheme.metadata?.name,
      selectedTheme.variables['theme-name'],
      selectedTheme.variables['--theme-name']
    ].filter((value): value is string => typeof value === 'string');

    return values.some((value) => /\bdark\b/i.test(value)) ? 'dark' : 'light';
  }

  private applyThemeToMapLayers(themeColor: ThemeColor): void {
    this._lastAppliedThemeColor = themeColor;

    const layers = store.map()?.layers;
    if (!layers?.forEach) return;

    this.applyThemeToLayerCollection(layers, themeColor);
  }

  private applyThemeToLayerCollection(layers: any, themeColor: ThemeColor): void {
    layers.forEach((layer: unknown) => {
      if (isThemeAwareLayer(layer)) {
        const previousTheme = layer.currentTheme;
        layer.applyTheme(themeColor);

        if (previousTheme === themeColor) {
          this.forceThemeAwareLayerRestyle(layer, themeColor);
        }
      }

      const childLayers = (layer as any)?.layers ?? (layer as any)?.subLayers?.();
      if (childLayers?.forEach) {
        this.applyThemeToLayerCollection(childLayers, themeColor);
      }
    });
  }

  /**
   * Restyle a layer that already reports the target theme.
   *
   * `applyTheme()` early-returns when the theme is unchanged, so a layer that claims the
   * theme but was never styled needs a direct nudge.
   *
   * `onThemeChange` and `updateLayerStyles` are alternatives, not a sequence: an
   * `onThemeChange` implementation is expected to restyle (NavigationPseudoLayer's clears
   * its style hash and calls `updateLayerStyles` itself), so calling both runs the whole
   * style pass twice. Mirrors the same helper in plugin-utils.
   */
  private forceThemeAwareLayerRestyle(
    layer: unknown,
    themeColor: ThemeColor
  ): void {
    const themeAwareLayer = layer as any;

    if (typeof themeAwareLayer.onThemeChange === 'function') {
      themeAwareLayer.onThemeChange(themeColor, themeColor);
      return;
    }

    if (typeof themeAwareLayer.updateLayerStyles === 'function') {
      themeAwareLayer.updateLayerStyles(themeColor);
    }
  }

  private setThemeAttribute(themeIdToApply: string): void {
    try {
      this._renderer.setAttribute(this._body, 'jmkw-theme', themeIdToApply);
    } catch (error) {
      this._logger.error(
        '[ThemeService] Failed to set theme attribute:',
        error
      );
    }
  }

  private injectCssVariables(
    selectedTheme: ThemeInfo,
    themeIdToApply: string
  ): void {
    try {
      if (selectedTheme.variables) {
        Object.entries(selectedTheme.variables).forEach(([key, value]) => {
          this._htmlElement.style.setProperty(key, value);
        });
        this._logger.debug(
          `[ThemeService] CSS variables injected for theme '${themeIdToApply}'`,
          {
            variableCount: Object.keys(selectedTheme.variables).length
          }
        );
      }
    } catch (error) {
      this._logger.error(
        '[ThemeService] Failed to inject CSS variables:',
        error
      );
    }
  }

  private applyCalciteMode(
    calciteMode: 'light' | 'dark',
    htmlEl: HTMLElement,
    bodyEl: HTMLElement
  ): void {
    htmlEl.classList.remove('calcite-mode-light', 'calcite-mode-dark');
    bodyEl.classList.remove('calcite-mode-light', 'calcite-mode-dark');

    htmlEl.classList.add(`calcite-mode-${calciteMode}`);
    bodyEl.classList.add(`calcite-mode-${calciteMode}`);

    htmlEl.setAttribute('data-calcite-theme', calciteMode);
    bodyEl.setAttribute('data-calcite-theme', calciteMode);
  }

  private buildCalciteVariables(
    selectedTheme: ThemeInfo,
    calciteMode: 'light' | 'dark'
  ): Record<string, string> {
    const calciteVariables: Record<string, string> = {};

    this._logger.debug('[ThemeService] Available theme variables:', {
      keys: Object.keys(selectedTheme.variables),
      sampleVars: Object.entries(selectedTheme.variables)
        .slice(0, 10)
        .map(([k, v]) => `${k}: ${v}`)
    });

    const primaryKey = 'primary';
    const backgroundKey = 'background';
    const textKey = 'text';
    const textInverseKey = 'text-inverse';

    if (primaryKey && selectedTheme.variables[primaryKey]) {
      const primaryColor = selectedTheme.variables[primaryKey];
      calciteVariables['--calcite-color-brand'] = primaryColor;
      calciteVariables['--calcite-ui-brand'] = primaryColor;
      this._logger.debug(`[ThemeService] Mapped primary color:`, {
        primaryKey,
        primaryColor
      });
    }

    if (backgroundKey && selectedTheme.variables[backgroundKey]) {
      const bgColor = selectedTheme.variables[backgroundKey];
      calciteVariables['--calcite-color-background'] = bgColor;
      calciteVariables['--calcite-ui-background'] = bgColor;
      calciteVariables['--calcite-color-foreground-1'] = bgColor;
      calciteVariables['--calcite-ui-foreground-1'] = bgColor;
      // foreground-2/3 intentionally not overridden — Calcite uses them for hover/active
      // backgrounds. Setting them equal to foreground-1 makes hover states invisible.
      this._logger.debug(`[ThemeService] Mapped background color:`, {
        backgroundKey,
        bgColor
      });
    }

    // NOTE: background-inverse should NOT be mapped to foreground colors
    // Foreground colors in Calcite are used for elevated surfaces (cards, panels)
    // and should be similar to the background, not inverted.
    // Mapping background-inverse causes dark shades in light mode.
    // if (backgroundInverseKey && selectedTheme.variables[backgroundInverseKey]) {
    //   const bgInvColor = selectedTheme.variables[backgroundInverseKey];
    //   calciteVariables['--calcite-color-foreground-1'] = bgInvColor;
    //   calciteVariables['--calcite-ui-foreground-1'] = bgInvColor;
    //   this._logger.debug(`[ThemeService] Mapped background-inverse color:`, {
    //     backgroundInverseKey,
    //     bgInvColor
    //   });
    // }

    if (textKey && selectedTheme.variables[textKey]) {
      const textColor = selectedTheme.variables[textKey];
      calciteVariables['--calcite-color-text-1'] = textColor;
      calciteVariables['--calcite-ui-text-1'] = textColor;
      calciteVariables['--calcite-color-text-2'] = textColor;
      calciteVariables['--calcite-ui-text-2'] = textColor;
      this._logger.debug(`[ThemeService] Mapped text color:`, {
        textKey,
        textColor
      });
    }

    if (textInverseKey && selectedTheme.variables[textInverseKey]) {
      const textInvColor = selectedTheme.variables[textInverseKey];
      calciteVariables['--calcite-color-inverse'] = textInvColor;
      calciteVariables['--calcite-ui-inverse'] = textInvColor;
      this._logger.debug(`[ThemeService] Mapped text-inverse color:`, {
        textInverseKey,
        textInvColor
      });
    }

    // NOTE: --calcite-color-border-1/2/3 are intentionally NOT overridden here.
    // Calcite uses these tokens for its own hover/focus indicator rings on interactive
    // components. Overriding them to a high-contrast color (e.g. the text color)
    // causes hover states to render with a dark ring that looks identical to the text,
    // breaking the hover UX. Widget container borders are applied via explicit CSS rules
    // in libs/core/src/styles/_calcite-static-overrides.scss, which reads the JMKW theme
    // tokens through var(). Those rules moved out of buildCalciteOverrideCss() in
    // JMKW-1860 because they carry no interpolated literals — edit them there, not here.

    if (!calciteVariables['--calcite-color-brand']) {
      calciteVariables['--calcite-color-brand'] =
        calciteMode === 'dark' ? '#009af2' : '#007ac2';
      this._logger.warn('[ThemeService] Using fallback primary color');
    }
    if (!calciteVariables['--calcite-color-background']) {
      calciteVariables['--calcite-color-background'] =
        calciteMode === 'dark' ? '#363636' : '#f7f7f7';
      this._logger.warn('[ThemeService] Using fallback background color');
    }
    if (!calciteVariables['--calcite-color-text-1']) {
      calciteVariables['--calcite-color-text-1'] =
        calciteMode === 'dark' ? '#ffffff' : '#141414';
      this._logger.warn('[ThemeService] Using fallback text color');
    }

    return calciteVariables;
  }

  private applyCalciteVariables(
    calciteVariables: Record<string, string>,
    htmlEl: HTMLElement,
    bodyEl: HTMLElement,
    calciteMode: 'light' | 'dark'
  ): void {
    Object.entries(calciteVariables).forEach(([key, value]) => {
      htmlEl.style.setProperty(key, value);
      bodyEl.style.setProperty(key, value);
    });

    this._logger.debug(
      `[ThemeService] Calcite theme mode set to '${calciteMode}'`,
      {
        appliedClass: `calcite-mode-${calciteMode}`,
        appliedVariables: Object.keys(calciteVariables).length,
        appliedTo: ['html', 'body']
      }
    );
  }

  private applyCalciteLightDom(calciteVariables: Record<string, string>): void {
    this.ensureCalciteMutationObserver();

    requestAnimationFrame(() => {
      const varsString = Object.entries(calciteVariables)
        .map(([key, value]) => `${key}: ${value} !important;`)
        .join('\n');

      const styleEl = this.ensureCalciteStyleElement();
      styleEl.textContent = this.buildCalciteOverrideCss(
        varsString,
        calciteVariables
      );

      this.injectZoomButtonShadowFix();
    });
  }

  /**
   * Mirrors body's active calcite-mode class onto Esri-managed UI containers
   * that take a one-shot setCalciteModeClass call at attach time. Esri's view
   * code (modeUtils.setCalciteModeClass) reads --esri-calcite-mode-name from
   * body once when each container attaches and never re-reads, so .esri-ui
   * and .esri-view-attribution carry whatever mode was active at attach time
   * regardless of subsequent theme switches. This helper closes that gap by
   * re-applying the active mode class to those containers whenever the theme
   * changes or when a new container is detected by the MutationObserver.
   *
   * Mode is read from body's classList as the single source of truth, set by
   * applyCalciteMode() prior to this call.
   */
  private syncEsriUiCalciteTheme(): void {
    const mode: 'light' | 'dark' = document.body.classList.contains(
      'calcite-mode-dark'
    )
      ? 'dark'
      : 'light';
    const desired = `calcite-mode-${mode}`;
    const opposite = `calcite-mode-${mode === 'light' ? 'dark' : 'light'}`;
    const targets = ['.esri-ui', '.esri-view-attribution'];

    targets.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        el.classList.remove(opposite);
        el.classList.add(desired);
      });
    });
  }

  /**
   * Injects shadow-DOM overrides into each zoom calcite-button's shadow root.
   *
   * Calcite's constructed stylesheet sets styles on the internal `button`
   * element inside the shadow DOM that external CSS cannot override, including:
   *   - cursor: pointer  (we reset to default)
   *   - border / box-shadow  (we suppress — the host-level container border and
   *     the :not(:last-of-type) separator handle all visible borders)
   *   - hover text/icon color  (set here rather than on the host so that the
   *     host-level separator border-bottom color is not affected)
   *
   * CSS custom-property token overrides on the host element are unreliable
   * across environments when Calcite uses hard-coded values or re-asserts tokens
   * via adoptedStyleSheets after our stylesheet applies. Shadow-root injection
   * is the only approach that works consistently in all deployments.
   *
   * The id guard prevents duplicate injection across repeated theme changes.
   */
  private injectZoomButtonShadowFix(isRetry = false): void {
    const styleId = 'jmkw-zoom-button-fix';
    // Read the JMKW-resolved --calcite-color-inverse from body, where our
    // :root, html, body { ... !important } rule wins. Inheritance into the
    // shadow root reads from the host, which is a descendant of .esri-ui;
    // Calcite's class-rule on .esri-ui redeclares --calcite-color-inverse to
    // its own "inverse of mode" semantic, which doesn't match JMKW's
    // "text-on-brand-hover-bg" intent. Substituting a literal here bypasses
    // that redeclaration. Updated on every theme apply.
    const inverse =
      getComputedStyle(document.body)
        .getPropertyValue('--calcite-color-inverse')
        .trim() || '#fff';
    let anyMissingShadow = false;
    document.querySelectorAll('.esri-zoom calcite-button').forEach((el) => {
      const shadow = el.shadowRoot;
      if (!shadow) {
        anyMissingShadow = true;
        return;
      }
      // Re-inject on theme change so the literal stays current.
      const existing = shadow.getElementById(styleId);
      if (existing) existing.remove();
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
          button,
          button:hover,
          button:focus,
          button:active {
            cursor: default !important;
            border: none !important;
            box-shadow: none !important;
          }

          button:hover {
            color: ${inverse} !important;
            --calcite-color-text-1: ${inverse} !important;
            --calcite-ui-text-1: ${inverse} !important;
          }
        `;
      shadow.prepend(style);
    });

    // Single-shot rAF retry covers the case where ArcGIS inserts the
    // calcite-button before Calcite hydrates it (shadow root attached
    // post-insertion). Bounded by isRetry so we never recurse indefinitely
    // if hydration never completes. The Slow-3G probe (JMKW-1858) never
    // observed this path firing locally, but Esri timing may differ in
    // production environments — this is cheap insurance.
    if (anyMissingShadow && !isRetry) {
      requestAnimationFrame(() => this.injectZoomButtonShadowFix(true));
    }
  }

  private ensureCalciteStyleElement(): HTMLStyleElement {
    let styleEl = document.getElementById('jmkw-calcite-theme-override');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'jmkw-calcite-theme-override';
      document.head.appendChild(styleEl);
    }
    return styleEl as HTMLStyleElement;
  }

  /**
   * Builds the global CSS override stylesheet injected into <head> at runtime.
   *
   * WHY THIS IS IN TYPESCRIPT, NOT SCSS
   * ─────────────────────────────────────────────────────────────────────────
   * This method serves two purposes that require runtime generation:
   *
   * 1. DYNAMIC VARIABLE INJECTION — the `varsString` argument contains
   *    resolved color values derived from the active theme (e.g.
   *    `--calcite-color-brand: #007ac2 !important`). These values are not
   *    known at build time and must be written into the stylesheet each time
   *    the theme changes.
   *
   * 2. RESOLVED LITERAL RULES — a small set of hover styles must use the
   *    JMKW-resolved inverse color as a literal value because Calcite
   *    redeclares --calcite-color-inverse on synced .esri-ui containers with
   *    different semantics. Static rules that can safely inherit CSS custom
   *    properties live in libs/core/src/styles/_calcite-static-overrides.scss.
   *
   * See also: jmkw.component.scss, which handles the one case (thumbnail
   * visibility) that does not depend on cascade ordering.
   */
  private buildCalciteOverrideCss(
    varsString: string,
    vars: Record<string, string>
  ): string {
    // `--calcite-color-inverse` cannot be referenced via var() in descendants
    // of `.esri-ui` because Calcite's own `.calcite-mode-{light,dark}` rule
    // redeclares the property on `.esri-ui` with a different semantic value
    // ("inverse of mode" vs JMKW's "text-on-brand-hover-bg"). Inheritance
    // takes the nearest declaration, so descendants see Calcite's value and
    // !important on body cannot override that. Use the resolved JMKW literal
    // here. Other tokens (text-1, background, brand) keep var() because
    // Calcite's values align with JMKW's intent for those.
    const inverse = vars['--calcite-color-inverse'] ?? '#fff';
    return `
      :root, html, body {
        ${varsString}
      }

      /* syncEsriUiCalciteTheme() stamps calcite-mode-* onto these containers,
         which makes Calcite's own .calcite-mode-{light,dark} rule match them
         and declare its default token values directly on the element. A direct
         declaration on an element wins over inherited body values regardless
         of !important on the body rule, so descendants of .esri-ui would see
         Calcite's defaults instead of the active JMKW theme. Declaring the
         JMKW token values directly on these containers (with !important from
         varsString) closes that gap. */
      .esri-ui,
      .esri-view-attribution {
        ${varsString}
      }

      /*
       * STYLING STRATEGY
       * ─────────────────────────────────────────────────────────────────────
       * Neither calcite-button nor calcite-action export any ::part() shadow
       * DOM parts (confirmed from Calcite source). ::part() rules are silently
       * ignored. The ONLY way to reach inside the shadow boundary is through
       * inherited CSS custom properties.
       *
       * calcite-button background reads (in priority order):
       *   var(--calcite-button-background-color,           ← public component token
       *     var(--calcite-internal-button-background-color, ← set internally by kind/appearance
       *       var(--calcite-color-transparent)))
       *
       * Setting --calcite-button-background-color on the host cascades through
       * the shadow boundary and wins over Calcite's internal calculation.
       *
       * calcite-action hover background reads:
       *   var(--calcite-action-background-color-hover,     ← public component token
       *     var(--calcite-color-foreground-2))
       *
       * Setting this unconditionally on the host means the shadow DOM's own
       * .button:hover rule picks it up automatically.
       * ─────────────────────────────────────────────────────────────────────
       */

      .esri-zoom calcite-button:hover {
        --calcite-button-background-color: var(--calcite-color-brand) !important;
        background-color: var(--calcite-color-brand) !important;
        /* On hover the icon flips to the inverse color for contrast against
           the brand background. Light theme: dark text/icon → white on brand.
           Dark theme: light text/icon → dark on brand. Use JMKW-resolved
           literal — see comment at top of buildCalciteOverrideCss(). */
        color: ${inverse} !important;
        --calcite-icon-color: ${inverse} !important;
      }

      /* Attribution is view-owned in ArcGIS v5, and Esri puts both class names on a
         SINGLE element — <div class="esri-widget esri-view-attribution esri-attribution">
         — so this must be a compound selector, not a descendant one. Esri sets
         .esri-attribution background-color to var(--calcite-color-transparent-tint, #fff),
         which leaves a light banner when the app starts directly in dark mode.

         Kept here rather than moved to _calcite-static-overrides.scss (JMKW-1860):
         this rule arrived with JMKW-1877 after that audit was done, so it has not
         been classified as theme-reactive or static yet. */
      .esri-view-attribution.esri-attribution {
        background-color: var(--calcite-color-background) !important;
        color: var(--calcite-color-text-1) !important;
      }

      /* Basemap selector — toggle button (hover highlight only).
         The open state is signalled by the icon swapping to the ">>" collapse
         chevron, matching Esri's Expand widget. Keeping a brand highlight on
         .active as well left the button looking stuck once the list closed
         (JMKW-1876). */
      jmkw-basemap-selector .basemap-toggle:hover {
        background-color: var(--calcite-color-brand) !important;
        color: ${inverse} !important;
      }

      /* The selected basemap carries the same brand fill as hover. Font weight
         alone (set in the component SCSS) read as unstyled next to the hover
         state, so the current basemap looked unselected (JMKW-1876).

         Stays in runtime injection rather than moving to
         _calcite-static-overrides.scss with the other .basemap-item rules
         (JMKW-1860): it interpolates the JMKW-resolved inverse literal. */
      jmkw-basemap-selector .basemap-item:hover,
      jmkw-basemap-selector .basemap-item.active {
        background-color: var(--calcite-color-brand) !important;
        color: ${inverse} !important;
      }

    `;
  }

  private ensureCalciteMutationObserver(): void {
    if (this._calciteObserver) return;

    this._calciteObserver = new MutationObserver((mutations) => {
      // Re-run when Esri attaches Calcite-containing nodes (e.g. the .esri-zoom
      // widget) OR an Esri-managed mode container (.esri-ui, .esri-view-
      // attribution) after our initial theme apply. JMKW-1858 Slow-3G probe
      // showed the zoom widget arrives via late childList insertion. Attribution
      // in v5 is view-owned and not always Calcite-backed, so we must detect it
      // by selector too — otherwise a late-attached attribution bar would keep
      // its stale attach-time calcite-mode class. A previous calcite-hydrated
      // attribute-filter retry branch targeted a hydration race that never
      // materialized in the probe and was removed in JMKW-1858.
      const foundNew = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return false;
          const el = node as Element;
          return this.containsCalcite(el) || this.containsEsriModeContainer(el);
        })
      );

      if (foundNew) {
        this.syncEsriUiCalciteTheme();
        this.injectZoomButtonShadowFix();
      }

      // Scoped to body/documentElement because the body observer runs with
      // subtree: true — Esri toggles classes on descendants constantly, and those
      // are not host theme flips. resolveHostThemeColorFromDom() only ever reads
      // these two elements, so a mutation anywhere else cannot change its answer.
      const hostThemeChanged = mutations.some(
        (mutation) =>
          mutation.type === 'attributes' &&
          (mutation.target === document.body ||
            mutation.target === document.documentElement)
      );
      if (hostThemeChanged) {
        const hostThemeColor = this.resolveHostThemeColorFromDom();
        // Skip our own writes: applySelectedTheme sets the very attributes being watched,
        // so acting on an unchanged color would repeat the whole layer pass each switch.
        if (hostThemeColor && hostThemeColor !== this._lastAppliedThemeColor) {
          setThemeRuntimeCurrentTheme(hostThemeColor);
          this.applyThemeToMapLayers(hostThemeColor);
        }
      }
    });

    this._calciteObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'calcite-mode', 'jmkw-theme', 'data-theme'],
      childList: true,
      subtree: true
    });
    this._calciteObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'calcite-mode', 'jmkw-theme', 'data-theme']
    });
  }

  private resolveHostThemeColorFromDom(): ThemeColor | undefined {
    const candidates = [document.body, document.documentElement];

    const values = candidates.flatMap((element) => [
      element.classList.contains('calcite-mode-dark') ? 'dark' : undefined,
      element.getAttribute('calcite-mode'),
      element.getAttribute('jmkw-theme'),
      element.getAttribute('data-theme'),
      element.className
    ]);

    if (
      values.some(
        (value) => typeof value === 'string' && /\bdark\b/i.test(value)
      )
    ) {
      return 'dark';
    }
    if (
      values.some(
        (value) => typeof value === 'string' && /\blight\b/i.test(value)
      )
    ) {
      return 'light';
    }

    return undefined;
  }

  private containsCalcite(el: Element): boolean {
    if (el.tagName.toLowerCase().startsWith('calcite-')) return true;
    // Check all descendant elements for calcite- prefixed tags
    return Array.from(el.querySelectorAll('*')).some((child) =>
      child.tagName.toLowerCase().startsWith('calcite-')
    );
  }

  /**
   * True when `el` is an Esri-managed mode container (or contains one).
   * These containers receive setCalciteModeClass once at attach time from
   * Esri and need our class-sync re-applied when they arrive late.
   */
  private containsEsriModeContainer(el: Element): boolean {
    if (el.matches?.('.esri-ui, .esri-view-attribution')) return true;
    return !!el.querySelector?.('.esri-ui, .esri-view-attribution');
  }

  private applyBasemap(selectedTheme: ThemeInfo): void {
    if (!selectedTheme.metadata?.esriBasemap) return;

    try {
      this._basemapManager.setBasemapById(selectedTheme.metadata.esriBasemap);
    } catch (error) {
      this._logger.error('[ThemeService] Failed to set basemap:', error);
    }
  }

  public change(themeId?: string): void {
    if (themeId) {
      this.enabledAvailableThemes$.pipe(take(1)).subscribe((themes) => {
        const themeExists = themes.some((theme) => theme.id === themeId);
        if (themeExists) {
          this._selectedThemeId$.next(themeId);
          this.persistSelectedThemeId(themeId);
        } else {
          this._logger.error(
            `[ThemeService] Cannot change to theme '${themeId}' - theme not found.`,
            {
              requestedTheme: themeId,
              availableThemes: themes.map((t) => t.id)
            }
          );
        }
      });
    } else {
      this.getNextTheme$(this._selectedThemeId$.value).subscribe(
        (nextTheme) => {
          this._selectedThemeId$.next(nextTheme.id);
          this.persistSelectedThemeId(nextTheme.id);
        }
      );
    }
  }

  private getInitialTheme(): string {
    const stored = this.readStoredThemeId();
    const initial = stored ?? this._defaultThemeId;
    return initial;
  }

  /**
   * Read the stored theme id, tolerating environments where localStorage is unavailable.
   *
   * Safari (and some others) throw a SecurityError when localStorage is touched in private
   * browsing, rather than returning null. Unguarded, that throw happens while the
   * `_selectedThemeId$` field initializer is running, which fails ThemeService construction
   * and can prevent the map from rendering at all.
   *
   * @returns the stored theme id, or null when nothing is stored OR storage is unreachable
   */
  private readStoredThemeId(): string | null {
    return safeGetItem(SELECTED_THEME_STORAGE_KEY, (error) =>
      // WARN, not ERROR: private browsing is an expected environment, not a fault.
      this._logger.warn(
        '[ThemeService] localStorage unavailable, using default theme',
        error
      )
    );
  }

  /**
   * Persist the selected theme id, no-oping when storage is unavailable.
   *
   * setItem can throw even when getItem succeeded — a full quota is the common case — so
   * this is guarded independently of {@link readStoredThemeId}. Failing to persist a
   * preference must never break theme switching itself.
   */
  private persistSelectedThemeId(themeId: string): void {
    safeSetItem(SELECTED_THEME_STORAGE_KEY, themeId, (error) =>
      this._logger.debug(
        '[ThemeService] localStorage unavailable, theme preference not persisted',
        error
      )
    );
  }

  private getNextTheme$(themeId: string): Observable<ThemeInfo> {
    return this.enabledAvailableThemes$.pipe(
      map((enabledThemes) => {
        if (enabledThemes.length === 0) {
          this._logger.error('[ThemeService] No themes available for cycling');
          throw new Error('No themes available');
        }

        const currentIndex = enabledThemes.findIndex(
          (theme) => theme.id === themeId
        );

        if (currentIndex === -1) {
          this._logger.warn(
            `[ThemeService] Current theme '${themeId}' not found, starting from first theme`
          );
          return enabledThemes[0];
        }

        const nextIndex = (currentIndex + 1) % enabledThemes.length;
        return enabledThemes[nextIndex];
      }),
      take(1)
    );
  }
}
